import apiClient from "./api";

export const STATUS_ASSINATURA = {
    PENDENTE:     'PENDENTE',
    ATIVA:        'ATIVA',
    INADIMPLENTE: 'INADIMPLENTE',
    SUSPENSA:     'SUSPENSA',
    CANCELADA:    'CANCELADA',
};

/**
 * Cria a assinatura do Pro. metodo: 'pix' | 'cartao'. `plano` é o código (PROFISSIONAL ou PRO_ANUAL,
 * C5f); ausente, o backend cria o mensal.
 */
export async function criarAssinatura(metodo, plano) {
    const { data } = await apiClient.post('/assinatura/criar', {
        billingType: metodo,
        ...(plano ? { plano } : {}),
    });
    return data;
}

/**
 * Troca entre o Pro mensal e o anual (C5f). Em dia, vale a partir do próximo vencimento, sem cobrar
 * nada agora; com a primeira cobrança ainda não paga, a assinatura é refeita no plano novo. Recusada
 * em atraso ou suspensa (409 com a mensagem).
 */
export async function trocarPlano(plano) {
    const { data } = await apiClient.post('/assinatura/trocar-plano', { plano });
    return data;
}

/**
 * "Cancelar a renovação do Pro" (C5g). Em dia, o Pro continua até o fim do período pago e a resposta
 * traz `proAte`; pendente, em atraso ou suspensa por falta de pagamento, cancela na hora e a pessoa volta
 * ao Livre. Suspensa por estorno é recusada (409 com a mensagem). Não desativa o perfil: isso é o
 * "Desativar meu perfil", em Perfil profissional.
 */
export async function cancelarRenovacao() {
    const { data } = await apiClient.post('/assinatura/cancelar-renovacao');
    return data;
}

/**
 * Assinatura atual do usuário logado, ou null se ele nunca assinou.
 * O backend responde 204 quando não há assinatura — sem corpo.
 */
export async function obterAssinatura() {
    const resposta = await apiClient.get('/assinatura/me');
    return resposta.status === 204 ? null : resposta.data;
}

/**
 * O plano em que a profissional está agora, calculado pelo backend (PlanoProfissionalService):
 * `{ codigo, nome }`, com codigo PROFISSIONAL (Pro) ou LIVRE. Pro é a assinatura mais recente ATIVA,
 * ou INADIMPLENTE depois de ter pago ao menos uma mensalidade; o resto é Livre. A regra mora só lá:
 * o front pergunta em vez de deduzir do status da assinatura.
 *
 * Desde a C6 traz também `fundador` (ou null): o selo Fundador(a), com o Pro grátis e o preço com
 * desconto de cada plano do Pro já calculados pelo backend -- ver services/fundador.js. O plano do
 * Pro grátis vem como PROFISSIONAL.
 */
export async function obterMeuPlano() {
    const { data } = await apiClient.get('/assinatura/me/plano');
    return data;
}

/**
 * O resumo do plano (C8): `situacao` (a do card e do menu), `situacaoDoPlano` (a da Assinatura),
 * `pendencia` (o selo do menu), o plano, a assinatura, o contador, o selo Fundador(a) e a taxa. A
 * situação é calculada no backend, e as telas não deduzem nada do status. Null para quem não tem perfil
 * profissional (204). Ver services/situacaoPlano.js.
 */
export async function obterResumoDoPlano() {
    const resposta = await apiClient.get('/assinatura/me/resumo');
    return resposta.status === 204 ? null : resposta.data;
}

export async function listarPagamentos() {
    const { data } = await apiClient.get('/assinatura/me/pagamentos');
    return data;
}

/**
 * Reemite os dados da cobrança em aberto (QR Code do Pix ou invoiceUrl do checkout).
 * O QR Code é de uso único e expira, por isso é sempre rebuscado em vez de cacheado.
 */
export async function obterCobrancaAtual() {
    const { data } = await apiClient.get('/assinatura/me/cobranca-atual');
    return data;
}

export async function alterarMetodo(metodo) {
    const { data } = await apiClient.post('/assinatura/alterar-metodo', { billingType: metodo });
    return data;
}

/**
 * Avisa que o profissional saiu da tela de pagamento sem pagar ("Pagar depois"), para ele receber
 * o lembrete no sino. Quem decide se o lembrete vale é o backend, que confere a cobrança na Asaas
 * antes — o clique sozinho não prova que ninguém pagou.
 */
export async function avisarPagamentoAdiado() {
    await apiClient.post('/assinatura/pagamento-adiado');
}

// `cancelarAssinatura` saiu daqui junto com o botão que a chamava. Cancelar a assinatura põe o
// Profissional em INATIVO — é a mesma coisa que desativar o perfil profissional —, e passou a ter
// um caminho só: POST /usuario/perfil-profissional/desativar, em services/perfilProfissionalService.
// O endpoint /assinatura/cancelar continua existindo no backend; o que não existe mais é uma
// segunda porta na interface, com aviso mais fraco que a da zona de perigo.
